/**
 * The ERP audit trail for a single manufacturing sub-job.
 *
 * Laid out exactly like the PO line's Audit History timeline — date and time
 * on the left, a coloured event dot on a connecting rail, then the change
 * itself with who made it. Both panels answer the same question ("who changed
 * what, and when"), so reading one should teach you how to read the other.
 *
 * Rendered at a slightly smaller scale than the line-level panel because it
 * nests inside a sub-job row rather than owning a card of its own.
 */
import {
  CalendarClock,
  Cog,
  Edit3,
  PackageCheck,
  Pause,
  PlusCircle,
  User,
  Workflow,
} from 'lucide-react';
import type { JobEvent } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

interface JobEventTimelineProps {
  events: JobEvent[];
}

interface EventStyle {
  dotClass: string;
  badgeClass: string;
  Icon: React.ComponentType<{ className?: string }>;
}

/**
 * Icon and colour for an event, keyed off the label the backend resolved.
 *
 * Matched on the label rather than the raw action code because only evidenced
 * codes get a name — everything else arrives as "Amendment (7)" and takes the
 * neutral style, which is the honest thing to show for an event we cannot
 * describe.
 */
function getEventStyle(event: JobEvent): EventStyle {
  const label = (event.action_label || '').toLowerCase();

  if (label.includes('dispatch') || label.includes('despatch')) {
    return { dotClass: 'bg-emerald-500', badgeClass: 'bg-emerald-100 text-emerald-800', Icon: PackageCheck };
  }
  if (label.includes('process')) {
    return { dotClass: 'bg-sky-500', badgeClass: 'bg-sky-100 text-sky-800', Icon: Cog };
  }
  if (label.includes('split')) {
    return { dotClass: 'bg-indigo-500', badgeClass: 'bg-indigo-100 text-indigo-800', Icon: Workflow };
  }
  if (label.includes('promise') || label.includes('date')) {
    return { dotClass: 'bg-violet-500', badgeClass: 'bg-violet-100 text-violet-800', Icon: CalendarClock };
  }
  if (label.includes('hold')) {
    return { dotClass: 'bg-orange-500', badgeClass: 'bg-orange-100 text-orange-800', Icon: Pause };
  }
  if (label.includes('created')) {
    return { dotClass: 'bg-slate-500', badgeClass: 'bg-slate-100 text-slate-800', Icon: PlusCircle };
  }
  return { dotClass: 'bg-slate-300', badgeClass: 'bg-slate-100 text-slate-700', Icon: Edit3 };
}

/**
 * Time of day, or '' for a date-only value.
 *
 * Same guard the line-level timeline uses: rendering a date-only string as a
 * time produces a meaningless "01:00" from the UTC-to-BST shift.
 */
function formatTime(value: string): string {
  if (!value) return '';
  if (!value.includes('T') && !value.includes(':')) return '';
  return new Date(value).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

/**
 * Timeline of ERP amendment events for one sub-job.
 *
 * @param events - the job's events, oldest or newest first as the API returns them
 */
export default function JobEventTimeline({ events }: JobEventTimelineProps) {
  const { formatDate } = useFormatters();

  if (events.length === 0) return null;

  return (
    <div className="space-y-3">
      {events.map((event, index) => {
        const style = getEventStyle(event);
        return (
          <div key={event.id} className="flex gap-3">
            {/* Date and time, as the line-level panel stacks them */}
            <div className="min-w-[78px] text-xs">
              <div className="font-semibold text-dark tabular-nums">
                {formatDate(event.occurred_at)}
              </div>
              <div className="text-muted-foreground tabular-nums">
                {formatTime(event.occurred_at)}
              </div>
            </div>

            {/* Dot on a connecting rail */}
            <div className="flex flex-col items-center">
              <div
                className={`w-5 h-5 rounded-full ${style.dotClass} flex items-center justify-center text-white shadow-sm`}
              >
                <style.Icon className="h-3 w-3" />
              </div>
              {index < events.length - 1 && (
                <div className="w-0.5 flex-1 bg-border mt-1" />
              )}
            </div>

            {/* What changed, and who changed it */}
            <div className="flex-1 pb-1 min-w-0">
              <div className="mb-0.5 flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-dark text-xs">
                  {event.action_label}
                </span>
                {event.is_milestone && (
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${style.badgeClass}`}>
                    Milestone
                  </span>
                )}
              </div>

              {event.note && (
                <div className="text-dark text-xs font-medium break-words">
                  {event.note}
                </div>
              )}

              {event.user_name && (
                <div className="text-muted-foreground text-xs mt-0.5 flex items-center gap-1">
                  <User className="h-3 w-3 shrink-0" />
                  <span title={event.user_job_title}>{event.user_name}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
